import { CUSTOM_COLUMN, MAX_CUSTOM_FILTERS, TABLE_COLUMN_IDS, TIMELINE_GROUPS, TIMELINE_ZOOMS, customFilter } from "@astrum/shared";
import type {
  CustomField,
  CustomFilter,
  SavedView,
  SavedViewConfig,
  SavedViewFilters,
  SavedViewSort,
  SavedViewType,
  TableColumnId,
  TimelineGroup,
  TimelineZoom,
  ViewColumnId,
} from "@astrum/shared";
import type { Task } from "./api.js";
import { customSortValue, taskMatchesCustomFilters } from "./custom-fields.js";

/*
 * The pure parts of the table, calendar and timeline views, and of saved-view
 * serialisation. Kept free of React so the arithmetic (dates to pixels, tasks to
 * calendar cells, view to URL) is testable on its own.
 *
 * Dates are handled as whole *days* — an integer count of days since the Unix
 * epoch, in UTC. Task dates are calendar dates, stored as UTC midnight (see
 * NewTaskDialog), so a UTC day number is exactly the date a person picked in
 * every timezone. Local-time maths would shift a due date by one across the
 * dateline, which is the bug this avoids.
 */

const DAY_MS = 86_400_000;

/* ─────────────────────────────────── days ─────────────────────────────────── */

export function dayOf(iso: string): number {
  return Math.floor(Date.parse(iso) / DAY_MS);
}

/** `YYYY-MM-DD` for a day number. */
export function dayKey(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

/** The ISO instant the app stores for a calendar date. */
export function isoOfDay(day: number): string {
  return `${dayKey(day)}T00:00:00.000Z`;
}

export function todayDay(now: Date = new Date()): number {
  // The viewer's own calendar date, expressed as a UTC day number.
  return Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS);
}

/** Day number of the first of the month. `month` is 0-11. */
export function monthStartDay(year: number, month: number): number {
  return Math.floor(Date.UTC(year, month, 1) / DAY_MS);
}

export function dayParts(day: number): { year: number; month: number; date: number; weekday: number } {
  const d = new Date(day * DAY_MS);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth(), date: d.getUTCDate(), weekday: d.getUTCDay() };
}

/** The days a task occupies, inclusive. Start only or due only is a single day. */
export type Span = { start: number; end: number };

export function taskSpan(task: Pick<Task, "startAt" | "dueAt">): Span | null {
  const start = task.startAt ? dayOf(task.startAt) : null;
  const due = task.dueAt ? dayOf(task.dueAt) : null;
  if (start !== null && due !== null) return { start: Math.min(start, due), end: Math.max(start, due) };
  const only = start ?? due;
  return only === null ? null : { start: only, end: only };
}

/* ─────────────────────────────────── calendar ─────────────────────────────────── */

/** Whole Sunday-to-Saturday weeks covering the month, as day numbers. */
export function monthWeeks(year: number, month: number): number[][] {
  const first = monthStartDay(year, month);
  const gridStart = first - dayParts(first).weekday;
  const last = monthStartDay(year, month + 1) - 1;
  const weeks: number[][] = [];
  for (let day = gridStart; day <= last; day += 7) {
    weeks.push(Array.from({ length: 7 }, (_, i) => day + i));
  }
  return weeks;
}

/**
 * Which tasks show on which days. A task with both dates appears on every day
 * of its span; a task with one appears on that day; an undated task is not on
 * the calendar. Only days inside [from, to] are kept, so a long task does not
 * allocate cells for the years it spans outside the visible grid.
 */
export function bucketByDay<T extends Pick<Task, "startAt" | "dueAt">>(
  tasks: T[],
  from: number,
  to: number,
): Map<number, T[]> {
  const buckets = new Map<number, T[]>();
  for (const task of tasks) {
    const span = taskSpan(task);
    if (!span || span.end < from || span.start > to) continue;
    for (let day = Math.max(span.start, from); day <= Math.min(span.end, to); day++) {
      const list = buckets.get(day);
      if (list) list.push(task);
      else buckets.set(day, [task]);
    }
  }
  return buckets;
}

/** What a cell shows: the first `max` tasks and how many more are hidden ("+3 more"). */
export function cellOverflow<T>(entries: T[], max: number): { shown: T[]; more: number } {
  return { shown: entries.slice(0, max), more: Math.max(0, entries.length - max) };
}

/**
 * The dates after dragging a task to another day.
 *
 * A single-date task moves its date. A task with both dates keeps its length: the
 * whole span shifts by however far it was dragged, measured from the day that
 * was grabbed, so dragging the 3rd of a 3rd-5th task onto the 10th gives 10th-12th.
 */
export function rescheduleOnDrop(
  task: Pick<Task, "startAt" | "dueAt">,
  grabbedDay: number,
  droppedDay: number,
): { startAt?: string; dueAt?: string } | null {
  const delta = droppedDay - grabbedDay;
  if (delta === 0) return null;
  const patch: { startAt?: string; dueAt?: string } = {};
  if (task.startAt) patch.startAt = isoOfDay(dayOf(task.startAt) + delta);
  if (task.dueAt) patch.dueAt = isoOfDay(dayOf(task.dueAt) + delta);
  return Object.keys(patch).length ? patch : null;
}

/* ─────────────────────────────────── timeline ─────────────────────────────────── */

export const ZOOM_PX_PER_DAY: Record<TimelineZoom, number> = { week: 48, month: 16, quarter: 5 };

export function dayToX(day: number, origin: number, pxPerDay: number): number {
  return (day - origin) * pxPerDay;
}

export function xToDay(x: number, origin: number, pxPerDay: number): number {
  return origin + Math.floor(x / pxPerDay);
}

/** A bar covers its first through last day inclusive, so a one-day task is one day wide. */
export function barRect(span: Span, origin: number, pxPerDay: number): { left: number; width: number } {
  return { left: dayToX(span.start, origin, pxPerDay), width: (span.end - span.start + 1) * pxPerDay };
}

/** Whole days a pointer moved, rounded to the nearest day so bars snap to the grid. */
export function dragDays(deltaPx: number, pxPerDay: number): number {
  return Math.round(deltaPx / pxPerDay);
}

export type DragMode = "move" | "start" | "end";

/** The span after a drag; resizing can shrink a bar to one day but not invert it. */
export function applyDrag(span: Span, mode: DragMode, days: number): Span {
  if (mode === "move") return { start: span.start + days, end: span.end + days };
  if (mode === "start") return { start: Math.min(span.start + days, span.end), end: span.end };
  return { start: span.start, end: Math.max(span.end + days, span.start) };
}

/** The visible day range: the tasks' extent and today, padded so bars are not flush to the edge. */
export function timelineRange(spans: Span[], today: number, pad = 7): { origin: number; end: number } {
  let lo = today;
  let hi = today;
  for (const s of spans) {
    lo = Math.min(lo, s.start);
    hi = Math.max(hi, s.end);
  }
  return { origin: lo - pad, end: hi + pad };
}

export type Tick = { day: number; x: number; label: string; major: boolean };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Header ticks. Week zoom: one per day, majors on Mondays. Month zoom: one per
 * Monday. Quarter zoom: one per month start. Always bounded by the range, never
 * by the number of days in some larger span.
 */
export function timelineTicks(origin: number, end: number, zoom: TimelineZoom, pxPerDay: number): Tick[] {
  const ticks: Tick[] = [];
  for (let day = origin; day <= end; day++) {
    const p = dayParts(day);
    if (zoom === "week") {
      ticks.push({ day, x: dayToX(day, origin, pxPerDay), label: `${p.date}`, major: p.weekday === 1 || p.date === 1 });
    } else if (zoom === "month") {
      if (p.weekday === 1) ticks.push({ day, x: dayToX(day, origin, pxPerDay), label: `${p.date} ${MONTHS[p.month]}`, major: p.date <= 7 });
    } else if (p.date === 1) {
      ticks.push({ day, x: dayToX(day, origin, pxPerDay), label: `${MONTHS[p.month]} ${p.year}`, major: p.month % 3 === 0 });
    }
  }
  return ticks;
}

/** True when the blocker finishes after the task that waits on it has begun. */
export function dependencyConflict(blocker: Span, dependant: Span): boolean {
  return blocker.end > dependant.start;
}

export type TimelineGroupBlock<T> = { key: string; label: string; tasks: T[] };

/**
 * Groups for the timeline, in a stable order: named groups alphabetically (or by
 * the order sections arrive in), then the ungrouped bucket last.
 */
export function groupForTimeline<T extends Pick<Task, "sectionId" | "sectionName" | "projectId" | "projectName">>(
  tasks: T[],
  by: TimelineGroup,
): TimelineGroupBlock<T>[] {
  if (by === "none") return [{ key: "all", label: "", tasks }];
  const blocks = new Map<string, TimelineGroupBlock<T>>();
  for (const task of tasks) {
    const key = (by === "section" ? task.sectionId : task.projectId) ?? "";
    const label = (by === "section" ? task.sectionName : task.projectName) ?? (by === "section" ? "No section" : "No project");
    const block = blocks.get(key);
    if (block) block.tasks.push(task);
    else blocks.set(key, { key, label, tasks: [task] });
  }
  return [...blocks.values()].sort((a, b) => (a.key === "" ? 1 : b.key === "" ? -1 : a.label.localeCompare(b.label)));
}

export type TimelineRow =
  | { kind: "header"; key: string; label: string; y: number }
  | { kind: "task"; key: string; taskId: string; y: number };

/** Row positions, so bars and arrows can be placed without a DOM cell per row. */
export function layoutTimelineRows(
  blocks: TimelineGroupBlock<{ id: string }>[],
  rowHeight: number,
  headerHeight: number,
): { rows: TimelineRow[]; height: number; yOf: Map<string, number> } {
  const rows: TimelineRow[] = [];
  const yOf = new Map<string, number>();
  let y = 0;
  for (const block of blocks) {
    if (block.label) {
      rows.push({ kind: "header", key: `h:${block.key}`, label: block.label, y });
      y += headerHeight;
    }
    for (const task of block.tasks) {
      rows.push({ kind: "task", key: task.id, taskId: task.id, y });
      yOf.set(task.id, y);
      y += rowHeight;
    }
  }
  return { rows, height: y, yOf };
}

/** An elbow connector from the right edge of one bar to the left edge of another. */
export function arrowPath(x1: number, y1: number, x2: number, y2: number): string {
  const gap = 8;
  if (x2 >= x1 + gap * 2) {
    const mid = x1 + gap;
    return `M${x1},${y1} H${mid} V${y2} H${x2}`;
  }
  // The dependant starts before the blocker ends: route round the back.
  const out = x1 + gap;
  const back = x2 - gap;
  const midY = y1 + (y2 >= y1 ? 1 : -1) * 12;
  return `M${x1},${y1} H${out} V${midY} H${back} V${y2} H${x2}`;
}

/* ─────────────────────────────────── table ─────────────────────────────────── */

export const COLUMN_LABELS: Record<TableColumnId, string> = {
  title: "Title",
  status: "Status",
  priority: "Priority",
  owners: "Owner",
  due: "Due",
  start: "Start",
  project: "Project",
  section: "Section",
  milestone: "Milestone",
  tags: "Tags",
  checklist: "Checklist",
};

export const DEFAULT_COLUMN_WIDTHS: Record<TableColumnId, number> = {
  title: 320,
  status: 130,
  priority: 120,
  owners: 150,
  due: 130,
  start: 130,
  project: 160,
  section: 150,
  milestone: 160,
  tags: 180,
  checklist: 110,
};

export const DEFAULT_VISIBLE_COLUMNS: ViewColumnId[] = TABLE_COLUMN_IDS.filter((c) => c !== "section" && c !== "milestone");

const STATUS_ORDER = ["todo", "in_progress", "review", "done", "cancelled"];
const PRIORITY_ORDER = ["low", "normal", "medium", "high", "urgent"];

/** The value a column sorts on. Strings and numbers only; null sorts last whichever way. */
export function sortValue(task: Task, key: string, fields?: readonly CustomField[]): string | number | null {
  if (CUSTOM_COLUMN.test(key)) return customSortValue(task, key.slice(3), fields);
  switch (key) {
    case "title":
      return task.title.toLowerCase();
    case "status":
      return STATUS_ORDER.indexOf(task.status);
    case "priority":
      return PRIORITY_ORDER.indexOf(task.priority);
    case "owners":
      return task.assignees?.[0]?.displayName.toLowerCase() ?? task.assigneeName?.toLowerCase() ?? null;
    case "due":
      return task.dueAt ? Date.parse(task.dueAt) : null;
    case "start":
      return task.startAt ? Date.parse(task.startAt) : null;
    case "project":
      return task.projectCode?.toLowerCase() ?? null;
    case "section":
      return task.sectionName?.toLowerCase() ?? null;
    case "milestone":
      return task.milestoneName?.toLowerCase() ?? null;
    case "tags":
      return task.tags?.[0]?.name.toLowerCase() ?? null;
    case "checklist":
      return task.checklistTotal ? (task.checklistDone ?? 0) / task.checklistTotal : null;
    default:
      return null;
  }
}

/** A stable sort; empty cells go to the end in both directions. */
export function sortTasks<T extends Task>(tasks: T[], sort: SavedViewSort | null, fields?: readonly CustomField[]): T[] {
  if (!sort) return tasks;
  const sign = sort.dir === "asc" ? 1 : -1;
  return tasks
    .map((task, index) => ({ task, index, value: sortValue(task, sort.key, fields) }))
    .sort((a, b) => {
      if (a.value === null && b.value === null) return a.index - b.index;
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      const cmp = a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
      return cmp * sign || a.index - b.index;
    })
    .map((entry) => entry.task);
}

/** Click on a header: off -> asc -> desc -> off. */
export function nextSort(current: SavedViewSort | null, key: string): SavedViewSort | null {
  if (!current || current.key !== key) return { key, dir: "asc" };
  return current.dir === "asc" ? { key, dir: "desc" } : null;
}

export function clampWidth(width: number): number {
  return Math.max(60, Math.min(800, Math.round(width)));
}

/**
 * The slice of rows to mount for a scroll position. Fixed row height makes this
 * arithmetic rather than measurement, which is what keeps scrolling smooth.
 */
export function virtualWindow(
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  total: number,
  overscan = 8,
): { start: number; end: number; padTop: number; padBottom: number } {
  const first = Math.floor(scrollTop / rowHeight);
  const start = Math.max(0, first - overscan);
  const end = Math.min(total, Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan);
  return { start, end, padTop: start * rowHeight, padBottom: Math.max(0, (total - end) * rowHeight) };
}

/** Rows past this many are virtualised; fewer render plainly. */
export const VIRTUALISE_AFTER = 200;

/* ─────────────────────────────────── view state ─────────────────────────────────── */

export const VIEW_TYPES: SavedViewType[] = ["list", "board", "table", "calendar", "timeline"];

/** Everything a view is, independent of where it is stored (URL or saved view). */
export type ViewState = {
  view: SavedViewType;
  grouping?: string;
  filters: SavedViewFilters;
  sort: SavedViewSort | null;
  /** Visible table columns, in order. */
  columns: ViewColumnId[];
  widths: Partial<Record<string, number>>;
  zoom: TimelineZoom;
  timelineGroup: TimelineGroup;
};

type Patch = Record<string, string | undefined>;

const isOneOf = <T extends string>(list: readonly T[], value: string | null | undefined): value is T =>
  !!value && (list as readonly string[]).includes(value);

/** The URL parameters a view owns. Applying a saved view clears all of them first. */
export const VIEW_PARAM_KEYS = [
  "view", "group", "q", "status", "priority", "mine", "overdue", "tag",
  "cf", "sort", "cols", "widths", "zoom", "tgroup",
] as const;

export function parseSort(value: string | null | undefined): SavedViewSort | null {
  if (!value) return null;
  // The key may itself hold a colon (`cf:<fieldId>`), so the direction is after the last one.
  const cut = value.lastIndexOf(":");
  const key = value.slice(0, cut);
  const dir = value.slice(cut + 1);
  if (cut < 1 || (dir !== "asc" && dir !== "desc")) return null;
  return { key, dir };
}

const isViewColumn = (c: string): c is ViewColumnId => isOneOf(TABLE_COLUMN_IDS, c) || CUSTOM_COLUMN.test(c);

export function parseColumns(value: string | null | undefined): ViewColumnId[] {
  if (!value) return DEFAULT_VISIBLE_COLUMNS;
  const picked = value.split(",").filter(isViewColumn);
  const unique = [...new Set(picked)];
  // The title column is the row's handle; it cannot be hidden away.
  if (!unique.includes("title")) unique.unshift("title");
  return unique;
}

export function parseWidths(value: string | null | undefined): Partial<Record<string, number>> {
  const out: Partial<Record<string, number>> = {};
  if (!value) return out;
  for (const part of value.split(",")) {
    const cut = part.lastIndexOf(":");
    const id = part.slice(0, cut);
    const n = Number(part.slice(cut + 1));
    if (id && isViewColumn(id) && Number.isFinite(n)) out[id] = clampWidth(n);
  }
  return out;
}

/** `cf` carries the custom field filters as JSON; anything malformed is dropped rather than throwing. */
export function parseCustomFilters(value: string | null | undefined): CustomFilter[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => customFilter.safeParse(entry))
      .flatMap((r) => (r.success ? [r.data] : []))
      .slice(0, MAX_CUSTOM_FILTERS);
  } catch {
    return [];
  }
}

export function stateFromParams(params: URLSearchParams, fallbackView: SavedViewType = "list"): ViewState {
  const view = params.get("view");
  const zoom = params.get("zoom");
  const tgroup = params.get("tgroup");
  const custom = parseCustomFilters(params.get("cf"));
  return {
    view: isOneOf(VIEW_TYPES, view) ? view : fallbackView,
    grouping: params.get("group") ?? undefined,
    filters: {
      ...(params.get("q") ? { q: params.get("q")! } : {}),
      ...(params.get("status") ? { status: params.get("status")! } : {}),
      ...(params.get("priority") ? { priority: params.get("priority")! } : {}),
      ...(params.get("mine") === "1" ? { mine: true } : {}),
      ...(params.get("overdue") === "1" ? { overdue: true } : {}),
      ...(params.get("tag") ? { tag: params.get("tag")! } : {}),
      ...(custom.length ? { custom } : {}),
    },
    sort: parseSort(params.get("sort")),
    columns: parseColumns(params.get("cols")),
    widths: parseWidths(params.get("widths")),
    zoom: isOneOf(TIMELINE_ZOOMS, zoom) ? zoom : "month",
    timelineGroup: isOneOf(TIMELINE_GROUPS, tgroup) ? tgroup : "section",
  };
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * The URL patch for a state. Every owned key is present: set to a value, or
 * `undefined` (deleted), so applying it replaces the previous view entirely
 * rather than merging into whatever filters happened to be on.
 */
export function paramsFromState(state: ViewState): Patch {
  const widthKeys = state.columns.filter((id) => state.widths[id] !== undefined);
  return {
    view: state.view,
    group: state.grouping,
    q: state.filters.q || undefined,
    status: state.filters.status || undefined,
    priority: state.filters.priority || undefined,
    mine: state.filters.mine ? "1" : undefined,
    overdue: state.filters.overdue ? "1" : undefined,
    tag: state.filters.tag || undefined,
    cf: state.filters.custom?.length ? JSON.stringify(state.filters.custom) : undefined,
    sort: state.sort ? `${state.sort.key}:${state.sort.dir}` : undefined,
    cols: sameList(state.columns, DEFAULT_VISIBLE_COLUMNS) ? undefined : state.columns.join(","),
    widths: widthKeys.length ? widthKeys.map((id) => `${id}:${state.widths[id]}`).join(",") : undefined,
    zoom: state.zoom === "month" ? undefined : state.zoom,
    tgroup: state.timelineGroup === "section" ? undefined : state.timelineGroup,
  };
}

/** What gets stored: the three jsonb columns of a saved view. */
export function toSavedPayload(state: ViewState): {
  config: SavedViewConfig;
  filters: SavedViewFilters;
  sort: SavedViewSort | null;
} {
  const widthKeys = state.columns.filter((id) => state.widths[id] !== undefined);
  return {
    config: {
      view: state.view,
      ...(state.grouping ? { grouping: state.grouping } : {}),
      columns: {
        visible: state.columns,
        ...(widthKeys.length ? { widths: Object.fromEntries(widthKeys.map((id) => [id, state.widths[id]!])) } : {}),
      },
      zoom: state.zoom,
      timelineGroup: state.timelineGroup,
    },
    filters: state.filters,
    sort: state.sort,
  };
}

export function stateFromSaved(view: Pick<SavedView, "config" | "filters" | "sort">): ViewState {
  const { config } = view;
  return {
    view: config.view,
    grouping: config.grouping,
    filters: view.filters ?? {},
    sort: view.sort ?? null,
    columns: config.columns?.visible?.length ? config.columns.visible.filter(isViewColumn) : DEFAULT_VISIBLE_COLUMNS,
    widths: (config.columns?.widths ?? {}) as Partial<Record<string, number>>,
    zoom: config.zoom ?? "month",
    timelineGroup: config.timelineGroup ?? "section",
  };
}

/**
 * Whether the screen still shows a saved view as saved. Compared through the URL
 * patch so there is one definition of "the same view": whatever the URL cannot
 * express is not part of a view.
 */
export function sameView(a: ViewState, b: ViewState): boolean {
  const pa = paramsFromState(a);
  const pb = paramsFromState(b);
  // Grouping only means something on the board, so it is ignored elsewhere.
  const keys = VIEW_PARAM_KEYS.filter((key) => key !== "widths" && (key !== "group" || a.view === "board"));
  if (!keys.every((key) => pa[key] === pb[key])) return false;
  // A width only counts for a column that is shown, in the table.
  return a.view !== "table" || a.columns.every((id) => a.widths[id] === b.widths[id]);
}

/* ─────────────────────────────────── editing ─────────────────────────────────── */

/**
 * The fields to PATCH after a timeline drag. Only the dates that moved are sent,
 * and a task that had a single date keeps a single date when it is moved whole.
 */
export function patchFromDrag(
  task: Pick<Task, "startAt" | "dueAt">,
  span: Span,
  mode: DragMode,
): { startAt?: string; dueAt?: string } {
  if (mode === "start") return { startAt: isoOfDay(span.start) };
  if (mode === "end") return { dueAt: isoOfDay(span.end) };
  return {
    ...(task.startAt ? { startAt: isoOfDay(span.start) } : {}),
    ...(task.dueAt ? { dueAt: isoOfDay(span.end) } : {}),
  };
}

/** Applies a view's saved filters to tasks already in memory (the project page has no server filter bar). */
export function filterTasks<T extends Task>(
  tasks: T[],
  filters: SavedViewFilters,
  viewerId: string | undefined,
  now = Date.now(),
  fields: readonly CustomField[] = [],
): T[] {
  const statuses = filters.status ? filters.status.split(",") : null;
  const priorities = filters.priority ? filters.priority.split(",") : null;
  const q = filters.q?.trim().toLowerCase();
  return tasks.filter((task) => {
    if (statuses && !statuses.includes(task.status)) return false;
    if (priorities && !priorities.includes(task.priority)) return false;
    if (q && !task.title.toLowerCase().includes(q)) return false;
    if (filters.mine && !(task.assignees?.some((a) => a.userId === viewerId) || task.assigneeId === viewerId)) return false;
    if (filters.overdue && !(task.dueAt && Date.parse(task.dueAt) < now && task.status !== "done" && task.status !== "cancelled")) return false;
    if (filters.tag && !(task.tags ?? []).some((tag) => filters.tag!.split(",").includes(tag.id))) return false;
    if (filters.custom?.length && !taskMatchesCustomFilters(task, filters.custom, fields)) return false;
    return true;
  });
}
